import { KsService, useApiClient, logger } from '@siclop/kstone-vue'
import { Wireframe } from '../models'
import type { WireframeForm } from '../forms'

export class WireframeService extends KsService {
  protected static mainUrl = '/wireframes'

  /**
   * Create a new wireframe
   */
  static async createWireframe(form: WireframeForm): Promise<Wireframe | null> {
    const client = useApiClient()
    const axios = client.getAxiosInstance()
    // API returns DataOperationResult<Wireframe> with { result: Wireframe, success: boolean, errors: [] }
    const response = await axios.post<{ result?: Wireframe; success?: boolean; errors?: string[] }>(this.mainUrl, form)
    const data = response.data
    if (!data || !data.success || !data.result) {
      const errorMsg = data?.errors?.join(', ') || 'Creation failed'
      throw new Error(errorMsg)
    }
    return new Wireframe(data.result)
  }

  /**
   * Delete a wireframe (soft delete)
   */
  static async deleteWireframe(id: number, force: boolean = false): Promise<void> {
    const client = useApiClient()
    const axios = client.getAxiosInstance()
    await axios.delete(`${this.mainUrl}/${id}/${force}`)
  }

  /**
   * Get all wireframes for a functionality
   */
  static async getByFunctionality(functionalityId: number): Promise<Wireframe[]> {
    const client = useApiClient()
    const data = await client.getAll<Wireframe>(`${this.mainUrl}/functionality/${functionalityId}`)
    return data.map(item => new Wireframe(item))
  }

  /**
   * Get all wireframes for a project
   */
  static async getByProject(projectId: number): Promise<Wireframe[]> {
    const client = useApiClient()
    const data = await client.getAll<Wireframe>(`${this.mainUrl}/project/${projectId}`)
    return data.map(item => new Wireframe(item))
  }

  /**
   * Get wireframe with full design system and components
   */
  static async getFull(id: number): Promise<Wireframe | null> {
    if (!id || id <= 0) {
      return null
    }
    const client = useApiClient()
    const data = await client.get<Wireframe>(`${this.mainUrl}/${id}/full`)
    if (!data) {
      return null
    }
    return new Wireframe(data)
  }

  /**
   * Reorder wireframes within a functionality
   */
  static async reorder(functionalityId: number, wireframeIds: number[]): Promise<void> {
    const client = useApiClient()
    await client.postRaw<number[], void>(`${this.mainUrl}/functionality/${functionalityId}/reorder`, wireframeIds)
  }

  /**
   * Update canvas data (component instances)
   */
  static async updateCanvasData(wireframeId: number, canvasDataJson: string): Promise<void> {
    if (!wireframeId || wireframeId <= 0) {
      throw new Error(`Invalid wireframe ID: ${wireframeId}`)
    }
    const client = useApiClient()
    const axios = client.getAxiosInstance()
    try {
      await axios.put(`${this.mainUrl}/${wireframeId}/canvas`, { canvasDataJson })
    } catch (error) {
      logger.error('updateCanvasData error:', error)
      throw error
    }
  }

  /**
   * Update thumbnail
   */
  static async updateThumbnail(wireframeId: number, thumbnail?: string): Promise<void> {
    const client = useApiClient()
    const axios = client.getAxiosInstance()
    await axios.put(`${this.mainUrl}/${wireframeId}/thumbnail`, { thumbnail })
  }

  /**
   * Clone a wireframe
   */
  static async clone(wireframeId: number, newName: string, targetFunctionalityId?: number): Promise<Wireframe | null> {
    const client = useApiClient()
    const data = await client.postRaw<{ newName: string; targetFunctionalityId?: number }, Wireframe>(
      `${this.mainUrl}/${wireframeId}/clone`,
      { newName, targetFunctionalityId }
    )
    return data ? new Wireframe(data) : null
  }
}
